import { useEffect, useState } from "react";
import { Copy, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { buildCreatorLink, type CreatorLinkKind } from "@/lib/ambassadors/creatorLinks";
import { getMe } from "@/lib/ambassadors/ambassadorEngine";
import { getOrCreateReferralCode } from "@/lib/referrals";
import { supabase } from "@/integrations/supabase/client";

const KINDS: { id: CreatorLinkKind; label: string }[] = [
  { id: "launch", label: "Launch" },
  { id: "showcase", label: "Showcase" },
  { id: "challenge", label: "Challenge" },
  { id: "takeover", label: "Takeover" },
];

/**
 * Single source of truth for the creator's nation = profiles.selection_team
 * (same field Nation Wars reads). No localStorage / no "MEX" default.
 * When NULL: input is disabled and shows "Selecciona tu nación".
 */
export function CreatorLinkBuilder() {
  const me = getMe();
  const [kind, setKind] = useState<CreatorLinkKind>("showcase");
  const [creator, setCreator] = useState("");
  const [nationProp, setNationProp] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("profiles")
        .select("selection_team")
        .eq("id", uid)
        .maybeSingle();
      if (alive) setNationProp((data?.selection_team as string | null) ?? null);
    })();
    return () => { alive = false; };
  }, []);
  const nation = nationProp ?? "";
  // Bloque C — referral unificado: mismo motor que Share Engine (Fase 1).
  const [refCode, setRefCode] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void getOrCreateReferralCode().then((c) => { if (alive) setRefCode(c); });
    return () => { alive = false; };
  }, []);
  const url = buildCreatorLink({
    kind,
    creator: creator || undefined,
    influencer: kind === "takeover" ? creator || undefined : undefined,
    nation: nation || undefined,
    ref: refCode ?? me.referralCode,
  });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <section className="rounded-2xl border border-border/60 bg-background/60 p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-black uppercase tracking-wider">
        <LinkIcon className="h-3.5 w-3.5" /> Creator Link Builder
      </h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <label className="text-[11px] font-bold">
          Tipo
          <select
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-2 py-1 text-xs"
            value={kind}
            onChange={(e) => setKind(e.target.value as CreatorLinkKind)}
          >
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-bold">
          Creator slug
          <input
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-2 py-1 text-xs"
            placeholder="mexico, creador…"
            value={creator}
            onChange={(e) => setCreator(e.target.value)}
          />
        </label>
        <label className="text-[11px] font-bold sm:col-span-2">
          Nación
          <input
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-2 py-1 text-xs disabled:opacity-60"
            value={nation}
            placeholder={nationProp ? "" : "Selecciona tu nación"}
            disabled
            readOnly
          />
          {!nationProp && (
            <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
              Elige tu nación en Nation Wars para activar tus enlaces de creador.
            </span>
          )}
        </label>
      </div>
      <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-dashed border-border/60 bg-muted/30 px-2 py-1.5 text-[11px]">
        <code className="flex-1 truncate">{url}</code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-black text-primary-foreground active:scale-95"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
    </section>
  );
}
